/**
 * Tenon Design System v1 — Token bindings (TS → CSS custom properties)
 *
 * CSS custom properties are the canonical source:
 *   web/src/design-system/tokens/{color,type,space,motion,status}.css
 *
 * This file provides TypeScript-typed access to those CSS vars. Every
 * value is a `var(--*)` reference — not a raw hex. The browser resolves
 * the actual color based on the [data-theme] attribute on <html>.
 *
 * Brand gold canonical: #C8B75D dark (muted olive) / #A8862C light (amber).
 *
 * Usage:
 *   import { tokens } from '@/design-system/tokens'
 *   style={{ color: tokens.color.brand.gold }}
 *   → renders as: color: var(--brand-gold)
 */

export const tokens = {
  color: {
    brand: {
      /** Primary brand gold — resolves to #C8B75D dark (muted olive) / #A8862C light (amber).
       *  Never use raw hex; var(--brand-gold) flips automatically with [data-theme]. */
      gold: 'var(--brand-gold)',
      goldLight: '#F5FB9B',
      goldDark: '#D4DB6B',
      /** Fluoro-yellow safety pop — CTAs, active states, highlights */
      fluoro: 'var(--brand-gold)',
      fluoroDark: '#D4DB6B',
      /** Subtle gold tint — focus rings, hover fills, active backgrounds */
      goldQuiet: 'var(--brand-gold-quiet)',
      /** Primary dark surface */
      navy: 'var(--brand-navy)',
      navyLight: '#1A1A1A',
    },

    /** Tenon-3 surface tiers (warm-black, dark-first).
     *  base = page background; surface / surface2 / surfaceHi = elevation layers. */
    surface: {
      base:      'var(--bg-page)',       // #0F0D09 dark / #F5F1E8 light
      surface:   'var(--bg-surface)',    // #171511 dark / #FBF7EE light
      surface2:  'var(--bg-surface2)',   // #1F1C16 dark / #EFEADC light
      surfaceHi: 'var(--bg-surface-hi)', // #27241D dark / #E6DFCB light
    },

    neutral: {
      white:   '#FFFFFF',
      50:      '#F8F9FA',
      100:     '#EEEEEE',
      200:     '#D8D8D8',
      300:     '#B0B0B0',
      400:     '#7A7A7A',
      500:     '#555555',
      600:     '#3D3D3D',
      700:     '#2E2E2E',
      800:     '#242424',
      900:     '#1A1A1A',
      black:   '#111111',
    },

    /** Canvas/raster markup palette — annotation drawing tools.
     *  These MUST stay raw hex: `ctx.strokeStyle` cannot resolve CSS vars,
     *  and saved annotations replay from stored color values, so they must
     *  be concrete and stable. Raw hex is allowed ONLY here in tokens.ts —
     *  pages/components import these, never inline hex (UI_GUIDELINES §2.2). */
    markup: {
      red:    '#EF4444',
      amber:  '#F59E0B',
      green:  '#22C55E',
      blue:   '#3B82F6',
      purple: '#A855F7',
      pink:   '#EC4899',
      white:  '#FFFFFF',
      /** Eraser paint — matches the legacy shop-design canvas backing so
       *  historical annotation replays render unchanged. */
      eraser: '#0F1117',
    },

    /** Status colours — CSS vars for theme-aware rendering.
     *  Legacy flat strings kept for backward compat.
     *  Tenon-3 per-state triples live under status.t3 and in CSS var sets
     *  (--st-<state>-dot / --st-<state>-bg / --st-<state>-text). */
    status: {
      // Legacy aliases (backward compat — consume via CSS vars)
      active:     'var(--st-active)',
      onHold:     'var(--st-onhold)',
      completed:  'var(--st-done)',
      cancelled:  'var(--st-blocked)',
      open:       'var(--st-info)',
      inProgress: 'var(--st-onhold)',
      done:       'var(--st-done)',
      draft:      'var(--st-draft)',

      /** Tenon-3 structured status triples — dot / bg / text per state.
       *  Use these for new Badge / Pill / StatusPill code. */
      t3: {
        not_started: {
          dot:  'var(--st-not-started-dot)',
          bg:   'var(--st-not-started-bg)',
          text: 'var(--st-not-started-text)',
        },
        in_progress: {
          dot:  'var(--st-in-progress-dot)',
          bg:   'var(--st-in-progress-bg)',
          text: 'var(--st-in-progress-text)',
        },
        blocked: {
          dot:  'var(--st-blocked-dot)',
          bg:   'var(--st-blocked-bg)',
          text: 'var(--st-blocked-text)',
        },
        completed: {
          dot:  'var(--st-completed-dot)',
          bg:   'var(--st-completed-bg)',
          text: 'var(--st-completed-text)',
        },
        on_hold: {
          dot:  'var(--st-on-hold-dot)',
          bg:   'var(--st-on-hold-bg)',
          text: 'var(--st-on-hold-text)',
        },
        cancelled: {
          dot:  'var(--st-cancelled-dot)',
          bg:   'var(--st-cancelled-bg)',
          text: 'var(--st-cancelled-text)',
        },
      },
    },

    /** Priority colours — legacy flat strings + Tenon-3 structured triples. */
    priority: {
      // Legacy aliases
      low:    'var(--st-draft)',
      medium: 'var(--st-info)',
      high:   'var(--st-onhold)',
      urgent: 'var(--st-blocked)',

      /** Tenon-3 structured priority triples */
      t3: {
        urgent: {
          dot:  'var(--pr-urgent-dot)',
          bg:   'var(--pr-urgent-bg)',
          text: 'var(--pr-urgent-text)',
        },
        high: {
          dot:  'var(--pr-high-dot)',
          bg:   'var(--pr-high-bg)',
          text: 'var(--pr-high-text)',
        },
        normal: {
          dot:  'var(--pr-normal-dot)',
          bg:   'var(--pr-normal-bg)',
          text: 'var(--pr-normal-text)',
        },
        low: {
          dot:  'var(--pr-low-dot)',
          bg:   'var(--pr-low-bg)',
          text: 'var(--pr-low-text)',
        },
      },
    },

    feedback: {
      success: 'var(--st-success)',
      warning: 'var(--st-warning)',
      error:   'var(--st-error)',
      info:    'var(--st-info)',
      review:  'var(--st-review)',
    },

    /** Tenon-3 avatar tints — deterministic by initial pair.
     *  Keys are two-letter uppercase initials (first + last).
     *  Fallback: use avatarColorFromName() helper below. */
    avatar: {
      JM: { bg: '#C8C2F3', ink: '#2A2452' },
      MC: { bg: '#D5CCF1', ink: '#3A2E66' },
      AM: { bg: '#F4D29A', ink: '#5A3E10' },
      RT: { bg: '#F0B5B0', ink: '#5C1E1A' },
      TG: { bg: '#A8DDC0', ink: '#1F4F33' },
      UN: { bg: '#F6C7C2', ink: '#5C1E1A' },
      DM: { bg: '#FFE091', ink: '#5A4810' },
    } as Record<string, { bg: string; ink: string }>,

    /** Legacy random palette — kept for backward compat with TaskForm.
     *  @deprecated — prefer tokens.color.avatar keyed lookup. */
    userPalette: [
      '#60A5FA', '#A78BFA', '#34D399', '#F59E0B',
      '#F472B6', '#FB923C', '#22D3EE', '#EF4444',
    ] as const,

    /** Gantt / schedule task type color map — keyed by task type label string.
     *  Maintained here so ScheduleGanttView.tsx stays hex-free. */
    taskType: {
      'Custom Build':        '#6366F1',
      'Callback':            '#EC4899',
      'Closeout':            '#EC4899',
      'Callback / Closeout': '#EC4899',
      'Office':              '#14B8A6',
      'Maintenance':         '#F97316',
      'Field':               '#8B5CF6',
      'Construction Task':   '#8B5CF6',
      'Service':             '#06B6D4',
      'Pre-Construction':    '#8B5CF6',
      // Back-compat aliases — data rows that haven't been re-read after the
      // rename migration may still carry the legacy type string.
      'Preconstruction':     '#8B5CF6',
      'Project Task':        '#8B5CF6',
    } as Record<string, string>,

    /** Entity-type accents (TaskForm pills) — theme-aware via CSS vars. */
    entity: {
      client: 'var(--entity-client)',
      sub:    'var(--entity-sub)',
      vendor: 'var(--entity-vendor)',
      ext:    'var(--entity-ext)',
    },

    background: {
      page:    'var(--bg-page)',
      surface: 'var(--bg-surface)',
      elevated:'var(--bg-elevated)',
      overlay: 'rgba(0, 0, 0, 0.7)',
      subtle:  'var(--bg-subtle, var(--bg-surface))',
    },

    text: {
      // Tenon-3 named tiers
      body:      'var(--text-primary)',    // warm off-white body text
      muted:     'var(--text-muted)',      // 0.62 alpha
      dim:       'var(--text-dim)',        // 0.40 alpha (very subdued)
      // Legacy aliases (backward compat)
      primary:   'var(--text-primary)',
      secondary: 'var(--text-secondary)',
      disabled:  'var(--text-disabled, #475569)',
      inverse:   'var(--text-inverse, #111111)',
      brand:     'var(--brand-gold)',
    },

    border: {
      // Tenon-3 tier names
      hairline: 'var(--border)',           // rgba(255,247,228,0.07) dark
      strong:   'var(--border-strong)',    // rgba(255,247,228,0.14) dark
      // Legacy aliases (backward compat)
      default: 'var(--border)',
      muted:   'var(--border-muted)',
      brand:   'var(--brand-gold)',
      focus:   'var(--brand-gold)',
      subtle:  'var(--border-subtle, var(--bg-surface))',
    },

    /** Tenon-3 row hover tint — apply as background on <tr> / list row hover. */
    rowHover: 'var(--row-hover)',
  },

  spacing: {
    px:  '1px',
    0:   '0',
    0.5: '0.125rem',
    1:   '0.25rem',
    2:   '0.5rem',
    3:   '0.75rem',
    4:   '1rem',
    5:   '1.25rem',
    6:   '1.5rem',
    8:   '2rem',
    10:  '2.5rem',
    12:  '3rem',
    16:  '4rem',
    20:  '5rem',
    24:  '6rem',
  },

  typography: {
    fontFamily: {
      sans: '"Karla", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
    },
    fontSize: {
      xs:   '0.75rem',
      sm:   '0.875rem',
      base: '1rem',
      lg:   '1.125rem',
      xl:   '1.25rem',
      '2xl':'1.5rem',
      '3xl':'1.875rem',
      '4xl':'2.25rem',
    },
    fontWeight: {
      normal:   '400',
      medium:   '500',
      semibold: '600',
      bold:     '700',
      extrabold:'800',
    },
    lineHeight: {
      tight:  '1.25',
      normal: '1.5',
      relaxed:'1.75',
    },
  },

  /** Top-level font aliases — shorthand for common usage. */
  font: {
    sans: '"Karla", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
  },

  radius: {
    none:  '0',
    sm:    '0.25rem',
    base:  '0.375rem',
    md:    '0.5rem',
    lg:    '0.75rem',
    xl:    '1rem',
    full:  '9999px',
  },

  shadow: {
    sm:    'var(--shadow-1)',
    md:    'var(--shadow-2)',
    lg:    'var(--shadow-3)',
    xl:    '0 20px 25px rgba(0,0,0,0.3)',
    brand: '0 0 0 3px var(--brand-gold-quiet)',
    focus: '0 0 0 3px var(--brand-gold-quiet)',
  },

  transition: {
    fast:   '100ms ease',
    base:   '150ms ease',
    slow:   '300ms ease',
  },

  zIndex: {
    base:    0,
    raised:  10,
    dropdown:100,
    sticky:  200,
    modal:   300,
    toast:   400,
    /** §mobile-bottom-anchored-overlay-zindex — clears MobileNav's zIndex:202.
     *  Use for the backdrop of any mobile position:fixed;inset:0 overlay. */
    mobileOverlayBackdrop: 290,
    /** Use for the sheet/panel content, or as the single value on a combined
     *  backdrop+panel div. See guide-mobile-ux.md §mobile-bottom-anchored-overlay-zindex. */
    mobileOverlayPanel: 300,
  },
} as const

export type Tokens = typeof tokens

/** Look up a Tenon-3 avatar tint by initials string (e.g. "Joe Meadows" → "JM").
 *  Falls back to a deterministic hue from a warm-toned fallback palette. */
export function avatarColorFromName(name: string): { bg: string; ink: string } {
  const parts = name.trim().split(/\s+/)
  const key = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase()

  const known = tokens.color.avatar[key]
  if (known) return known

  // Deterministic fallback — hash name into one of 8 warm-palette tints
  const fallbacks: Array<{ bg: string; ink: string }> = [
    { bg: '#C8C2F3', ink: '#2A2452' },
    { bg: '#F4D29A', ink: '#5A3E10' },
    { bg: '#A8DDC0', ink: '#1F4F33' },
    { bg: '#F0B5B0', ink: '#5C1E1A' },
    { bg: '#FFE091', ink: '#5A4810' },
    { bg: '#F6C7C2', ink: '#5C1E1A' },
    { bg: '#D5CCF1', ink: '#3A2E66' },
    { bg: '#B8D8F0', ink: '#1A3A56' },
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return fallbacks[Math.abs(hash) % fallbacks.length]
}

// CSS custom properties — inject via index.css root selector
export const cssVariables = `
  --color-brand-gold: ${tokens.color.brand.gold};
  --color-brand-gold-light: ${tokens.color.brand.goldLight};
  --color-brand-gold-dark: ${tokens.color.brand.goldDark};
  --color-brand-fluoro: ${tokens.color.brand.fluoro};
  --color-brand-navy: ${tokens.color.brand.navy};
  --color-bg-page: ${tokens.color.background.page};
  --color-bg-surface: ${tokens.color.background.surface};
  --color-bg-elevated: ${tokens.color.background.elevated};
  --color-text-primary: ${tokens.color.text.primary};
  --color-text-secondary: ${tokens.color.text.secondary};
  --color-text-muted: ${tokens.color.text.muted};
  --color-border: ${tokens.color.border.default};
  --font-sans: ${tokens.typography.fontFamily.sans};
  --font-mono: ${tokens.typography.fontFamily.mono};
`

// ────────────────────────────────────────────────
// Light Mode Tokens — Tenon-3 warm-sand palette
// ────────────────────────────────────────────────
export const lightTokens = {
  color: {
    background: {
      page:    '#F5F1E8',
      surface: '#FBF7EE',
      elevated:'#EFEADC',
      overlay: 'rgba(0, 0, 0, 0.4)',
      subtle:  '#FBF7EE',
    },
    text: {
      primary:   '#1A1610',
      secondary: 'rgba(26,22,16,0.62)',
      muted:     'rgba(26,22,16,0.40)',
      disabled:  'rgba(26,22,16,0.28)',
      inverse:   '#F5F1E8',
      brand:     '#C99E35',
    },
    border: {
      default: 'rgba(40,30,15,0.09)',
      muted:   'rgba(40,30,15,0.06)',
      brand:   '#C99E35',
      focus:   '#C99E35',
      strong:  'rgba(40,30,15,0.16)',
      subtle:  '#EFEADC',
    },
    brand: {
      gold:      '#C99E35',
      goldLight: '#F0C96A',
      goldDark:  '#A07E20',
      fluoro:    '#C99E35',
      fluoroDark:'#A07E20',
      navy:      '#1A1610',
      navyLight: '#1A1A1A',
    },
  },
} as const
